import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FormContext, useForm } from "react-hook-form";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  styled,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { APIError } from "../../components/APIError";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";
import { DropdownSelector } from "../../components/Forms";
import { useScrollTo } from "../../hooks/useScrollTo";
import { ModalComponent } from "../../pages/routes";
import { ContactType } from "./components/ContactType";
import { ContactDetails } from "./components/ContactDetails";
import { ContactAddress } from "./components/ContactAddress";
import { AlertBlock } from "../../components/AlertBlock";
import {
  CancelButton,
  CloseButton,
  ConfirmButton,
  CustomDialog,
} from "../../components/CustomDialog";
import { useContactConfig } from "./hooks/useContactConfig";
import { useAccountContacts } from "../../api/hooks";
import { useAccountContactsUpdate } from "./hooks/useAccountContactsUpdate";
import { AccountContact } from "../../api/services";
import { formatContactName } from "../../utils/formatName";
import { FIELD_REQUIRED_MSG } from "../../utils/validation";
import { CustomerIcon as BaseCustomerIcon } from "../../icons";
import { theme } from "../../theme";
import { DialogLoader } from "../../components/Loader";

const CustomerIcon = styled(BaseCustomerIcon)(({ theme }) => ({
  fill: theme.palette.grey["900"],
  marginRight: theme.spacing(0.75),
}));

export interface ContactEditDetails {
  id: string;
  type: string;
  title: string;
  forename: string;
  surname: string;
  number1: string;
  number2: string;
  number3: string;
  email: string;
  dateOfBirthDt: string;
  securityNumber: string;
}

const fieldDefaultProps: Partial<TextFieldProps> = {
  variant: "outlined",
  fullWidth: true,
  size: "small",
};

export const ContactDialog: ModalComponent = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const { confirmOverlay, closeOverlay } = useNavigator();
  const { accountNumber, contactId } = useParams();
  const { editContact, error: updateError } = useAccountContactsUpdate(
    accountNumber,
  );

  const {
    data: config,
    error: configError,
    status: configStatus,
  } = useContactConfig();
  const {
    data: contacts,
    error: contactsError,
    status: contactsStatus,
  } = useAccountContacts(accountNumber);

  const contact: AccountContact | undefined = useMemo(() => {
    return contacts?.find((contact) => contact.id === contactId);
  }, [contacts, contactId]);

  const formMethods = useForm<ContactEditDetails>({
    mode: "onBlur",
  });
  const { handleSubmit, register, watch, errors, formState } = formMethods;
  const onSubmit = useCallback(
    async (values) => {
      const response = await editContact(values);
      if (response?.status === 204) {
        confirmOverlay(`/accounts/${accountNumber}`, {
          success: "Contact details saved.",
        });
      }
    },
    [accountNumber, confirmOverlay, editContact],
  );
  const selectedType = watch("type") ?? contact?.type;

  const [scrollableContentRef, handleScrollToTop] = useScrollTo();

  return (
    <FormContext {...formMethods}>
      <CustomDialog
        data-testid="ContactDialog"
        onClose={closeOverlay}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit(onSubmit),
          // @ts-ignore
          noValidate: true,
          id: "ContactDialog__Form",
        }}
      >
        <DialogTitle
          disableTypography={true}
          data-testid="ContactDialog__Title"
        >
          <Typography variant="h5">Edit contact</Typography>
          <Grid container alignItems="center">
            <CustomerIcon height={16} width={16} />
            <Typography variant="body1">
              {contactsStatus === "loading" ? (
                <Skeleton variant="text" animation="wave" width={100} />
              ) : contact ? (
                <>{formatContactName(contact)}</>
              ) : (
                "Unknown contact"
              )}
            </Typography>
          </Grid>
          <Box
            position="absolute"
            mt="-2px"
            right={theme.spacing(2)}
            top={theme.spacing(2.5)}
          >
            <CloseButton />
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          data-testid="ContactDialog__Content"
          ref={scrollableContentRef}
        >
          {updateError && (
            <Box mb={3}>
              <APIError
                onMount={handleScrollToTop}
                title="Failed to save update to contact."
                messages={[(updateError as Error).message]}
              />
            </Box>
          )}
          {configError || contactsError ? (
            <APIError
              title="We can't load details for this contact at the moment."
              mb={30}
            />
          ) : configStatus === "loading" || contactsStatus === "loading" ? (
            <DialogLoader mb={30} />
          ) : config && contact ? (
            <>
              <input
                type="hidden"
                name="id"
                value={contact!.id}
                ref={register}
              />
              <ContactType
                defaultValue={contact!.type.name}
                types={config!.types.map((type) => ({
                  name: type.name,
                  value: type.name,
                }))}
              />

              {/* Contact Name */}
              <Box maxWidth={260} mt={4}>
                <DropdownSelector
                  name="title"
                  options={config!.titles.map((title) => ({
                    name: title,
                    value: title,
                  }))}
                  controllerProps={{ defaultValue: contact!.title }}
                  fieldProps={{
                    label: "Title",
                    fullWidth: true,
                    error: !!errors.title,
                    helperText: errors?.title ? FIELD_REQUIRED_MSG : undefined,
                    "data-testid": "ContactDialog__TextField--Title",
                    required: true,
                  }}
                  allowEmpty
                />
              </Box>
              <Box mt={4.5}>
                <Grid container justify="space-between">
                  <Box maxWidth={260} width={0.49}>
                    <TextField
                      name="forename"
                      label="Forename"
                      {...fieldDefaultProps}
                      error={!!errors?.forename}
                      helperText={errors?.forename?.message}
                      inputRef={register({
                        required: "This field is required",
                      })}
                      required
                      defaultValue={contact!.forename}
                    />
                  </Box>
                  <Box maxWidth={260} width={0.49}>
                    <TextField
                      name="surname"
                      label="Surname"
                      {...fieldDefaultProps}
                      error={!!errors?.surname}
                      helperText={errors?.surname?.message}
                      inputRef={register({
                        required: "This field is required",
                      })}
                      required
                      defaultValue={contact!.surname}
                    />
                  </Box>
                </Grid>
              </Box>

              {/* Contact Details */}
              <Box mt={5}>
                <ContactDetails
                  contact={contact!}
                  fieldProps={{
                    ...fieldDefaultProps,
                    inputRef: register,
                  }}
                  type={config!.types.find((type) => {
                    return type.name === selectedType;
                  })}
                />
              </Box>
              {/* Billing Address */}
              <Box mt={4.5}>
                <ContactAddress address={contact.address} />
              </Box>
            </>
          ) : (
            <Box mb={30}>
              <AlertBlock
                variant="error"
                title="We can't load details for this contact at the moment."
                messages={["This contact does not exist."]}
                isClosable={false}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions data-testid="ContactDialog__Actions">
          <CancelButton data-testid="ContactDialog__Button--cancel" />
          <ConfirmButton
            data-testid="ContactDialog__Button--submit"
            type="submit"
            disabled={
              configStatus === "loading" ||
              contactsStatus === "loading" ||
              !!configError ||
              !contact ||
              formState.isSubmitting
            }
          >
            Save
          </ConfirmButton>
        </DialogActions>
      </CustomDialog>
    </FormContext>
  );
};
